import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Upload, Brain, FileSpreadsheet, BarChart3, MessageSquare, 
  Users, Shield, Check, ArrowRight, Star, Zap, CreditCard,
  Globe, Clock, TrendingUp
} from 'lucide-react';
import Logo from '../components/Logo';

const FEATURES = [
  {
    icon: Upload,
    title: 'Smart Invoice Upload',
    description: 'Drag & drop PDFs, photos, or screenshots. We support all major formats.',
    color: 'bg-blue-500'
  },
  {
    icon: Brain,
    title: 'AI Data Extraction',
    description: 'Automatically extract vendor, amount, GST, dates, and more with AI.',
    color: 'bg-purple-500'
  },
  {
    icon: FileSpreadsheet,
    title: 'Export to Any Format',
    description: 'Excel, Xero, MYOB, QuickBooks, and ATO-ready BAS reports. Export your way.',
    color: 'bg-emerald-500'
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description: 'Track spending trends, vendor analysis, and get budget alerts.',
    color: 'bg-amber-500'
  },
  {
    icon: Globe,
    title: 'Tax Office Ready',
    description: 'Generate ATO BAS-ready GST summaries. Built for Australian tax compliance.',
    color: 'bg-red-500'
  },
  {
    icon: Users,
    title: 'Grows With You',
    description: 'Start free, scale as you grow. Add team members when you need them.',
    color: 'bg-cyan-500'
  }
];

const PRICING = [
  {
    name: 'Free',
    price: '0',
    period: '',
    description: 'Perfect for getting started',
    features: ['10 invoices/month', '1 user', 'AI extraction', 'Excel export'],
    cta: 'Start Free',
    popular: false,
    color: 'border-slate-200'
  },
  {
    name: 'Pro',
    price: '4.99',
    period: '/month',
    gst: '+ A$0.50 GST',
    description: 'For growing businesses',
    features: ['100 invoices/month', '5 team members', 'Analytics dashboard', 'AI chat assistant', 'PDF/Word export', 'Multi-currency'],
    cta: 'Start 14-Day Trial',
    popular: true,
    color: 'border-emerald-500'
  },
  {
    name: 'Team',
    price: '19.99',
    period: '/month',
    gst: '+ A$2.00 GST',
    description: 'For teams that need everything',
    features: ['Unlimited invoices', 'Unlimited team members', 'Audit logs', 'QuickBooks export', 'Priority support', 'API access'],
    cta: 'Start 14-Day Trial',
    popular: false,
    color: 'border-amber-500'
  }
];

const TESTIMONIALS = [
  {
    quote: "Expense Flow has saved us hours every week on invoice processing. The AI extraction is incredibly accurate!",
    author: "Sarah Chen",
    role: "Finance Manager, TechStart",
    avatar: "SC"
  },
  {
    quote: "Finally, an expense tool that understands Australian GST! The analytics help me make better business decisions.",
    author: "Michael Roberts",
    role: "Small Business Owner",
    avatar: "MR"
  },
  {
    quote: "The team collaboration features are exactly what we needed. Everyone can upload and I can track it all.",
    author: "Jessica Wong",
    role: "Operations Director",
    avatar: "JW"
  }
];

function LandingPage() {
  const [email, setEmail] = useState('');

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Logo size="md" />
              <span className="font-bold text-lg text-slate-900 hidden sm:block">Expense Flow</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Testimonials</a>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to="/login" 
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              >
                Log In
              </Link>
              <Link 
                to="/login" 
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Built for Small Business Owners
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Focus on Growing Your Business,
                <span className="text-emerald-600"> Not Paperwork</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                We do the heavy lifting so you can have peace of mind. AI extracts your invoice data, 
                tracks GST, and exports to Xero, MYOB, QuickBooks & ATO-ready formats. 
                <strong className="text-slate-800"> Scales as your business grows.</strong>
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/login"
                  className="w-full sm:w-auto px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
                >
                  Start Free — No Credit Card
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="#features"
                  className="w-full sm:w-auto px-8 py-4 border-2 border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-semibold text-lg transition-all"
                >
                  See How It Works
                </a>
              </div>

              <div className="mt-10 flex items-center justify-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  14-day free trial
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  GST compliant
                </div>
              </div>
            </motion.div>
          </div>

          {/* Hero Image/Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-16 relative"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-1 shadow-2xl shadow-emerald-500/20">
              <div className="bg-slate-900 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="bg-slate-800 rounded-lg p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-700 rounded-lg p-4">
                    <Upload className="w-8 h-8 text-emerald-400 mb-3" />
                    <p className="text-white font-medium">Drop Invoice</p>
                    <p className="text-slate-400 text-sm">PDF, JPG, PNG</p>
                  </div>
                  <div className="bg-slate-700 rounded-lg p-4">
                    <Brain className="w-8 h-8 text-purple-400 mb-3" />
                    <p className="text-white font-medium">AI Extracts Data</p>
                    <p className="text-slate-400 text-sm">Vendor, Amount, GST</p>
                  </div>
                  <div className="bg-slate-700 rounded-lg p-4">
                    <BarChart3 className="w-8 h-8 text-amber-400 mb-3" />
                    <p className="text-white font-medium">Track & Analyze</p>
                    <p className="text-slate-400 text-sm">Insights & Reports</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10,000+', label: 'Invoices Processed' },
              { value: '500+', label: 'Happy Businesses' },
              { value: '99.9%', label: 'Extraction Accuracy' },
              { value: '5 min', label: 'Avg. Time Saved' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Small Business Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Built for Small Business Owners, By People Who Get It
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                We know you didn&apos;t start your business to spend hours on paperwork. 
                Expense Flow does the heavy lifting — from scanning invoices to preparing tax reports — 
                so you can focus on what you do best.
              </p>
              <ul className="space-y-4">
                {[
                  'Start small, scale as you grow — no complex setup',
                  'ATO-ready exports for stress-free BAS time',
                  'Works with Xero, MYOB, QuickBooks & more',
                  'AI handles data entry, you just review'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-emerald-600 mb-2">5+ hrs</p>
                <p className="text-slate-600">saved per week on average</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-slate-900">99%</p>
                  <p className="text-sm text-slate-500">AI Accuracy</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-slate-900">30 sec</p>
                  <p className="text-sm text-slate-500">Per Invoice</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Manage Expenses
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From invoice upload to tax-ready exports, we&apos;ve got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600">Three simple steps to manage your expenses</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Upload', desc: 'Drop your invoice PDF, photo, or screenshot', icon: Upload },
              { step: '2', title: 'Extract', desc: 'AI automatically reads and extracts all data', icon: Brain },
              { step: '3', title: 'Manage', desc: 'Review, export, and track your expenses', icon: TrendingUp }
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-emerald-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">Start free, upgrade when you need more</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-white border-2 ${plan.color} rounded-2xl p-6 ${plan.popular ? 'shadow-xl scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-emerald-600 text-white text-sm font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-slate-500 text-sm mt-1">{plan.description}</p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900">A${plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                    {plan.gst && <p className="text-xs text-slate-400 mt-1">{plan.gst}</p>}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/login"
                  className={`block w-full py-3 rounded-lg font-semibold text-center transition-colors ${
                    plan.popular
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'border-2 border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            All prices in AUD. Annual plans available with 2 months free.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Loved by Small Businesses
            </h2>
            <p className="text-lg text-slate-600">See what our customers have to say</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 rounded-xl p-6"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4">&quot;{testimonial.quote}&quot;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Simplify Your Expense Management?
          </h2>
          <p className="text-lg text-emerald-100 mb-8">
            Join hundreds of Australian small businesses saving time with Expense Flow.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-700 rounded-xl font-semibold text-lg hover:bg-emerald-50 transition-colors"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-emerald-200 mt-4">No credit card required • 14-day free trial</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Logo size="sm" />
                <span className="font-bold">Expense Flow</span>
              </div>
              <p className="text-slate-400 text-sm">
                AI-powered expense management for Australian small businesses.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center text-slate-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Expense Flow for Small Biz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
